import { deepmergeCustom } from 'deepmerge-ts';
import { deepEqual } from 'fast-equals';
import { isDevMode } from '@angular/core';

/** @internal */
/* istanbul ignore next */
function camelToKebabCase(value) {
    if (value !== value.toLowerCase()) {
        return value.replace(/[A-Z]/g, (match, index) => {
            const char = match.toLowerCase();
            return index === 0 ? char : `-${char}`;
        });
    }
    return value;
}

/**
 * @internal
 * Generates a unique property name for a decorator.
 *
 * @param {string} decoratorName - The name of the decorator.
 * @param {string} propertyName - The name of the property.
 * @returns {string} - The unique property name.
 */
function decoratorPropertyName(decoratorName, propertyName) {
    return `__ODX_${decoratorName}_${propertyName}__`;
}

const deepmerge = deepmergeCustom({
    mergeArrays: false,
    mergeSets: false,
});

const fastDeepEqual = (obj1, obj2) => deepEqual(obj1, obj2);

/* istanbul ignore next */
function isSameDomain({ href }) {
    return !href || href.startsWith(window.location.origin);
}
/** @internal */
/* istanbul ignore next */
function getCSSVariables(filter, selector = ':root') {
    const stylesheets = Array.from(document.styleSheets).filter(isSameDomain);
    return stylesheets.reduce((variables, { cssRules }) => {
        const rules = Array.from(cssRules).filter(({ type }) => type === CSSRule.STYLE_RULE);
        for (const { style, selectorText } of rules) {
            for (const propName of Array.from(style)) {
                const name = propName.trim();
                if (!name.startsWith('--') || !filter.test(name) || (selector && selectorText !== selector))
                    continue;
                variables.push({ name, value: style.getPropertyValue(propName).trim() });
            }
        }
        return variables;
    }, []);
}

/** @internal */
function getEnumValues(e) {
    const keys = Object.keys(e);
    return keys.filter((key) => e[key] !== undefined).map((key) => e[key]);
}

/** @internal */
function toggleFormDisabledState(form) {
    form.disabled ? form.enable() : form.disable();
    console.log('form', form);
}

const CSS_COMPONENT_NAME = decoratorPropertyName('CSSComponent', 'name');
/** @internal */
/* istanbul ignore next */
function CSSComponent(name, prefix = 'odx') {
    return (target) => {
        const cssClass = `${prefix}-${name}`;
        Object.defineProperty(target.prototype, CSS_COMPONENT_NAME, {
            value: cssClass,
        });
        (function (ngOnInit) {
            target.prototype.ngOnInit = function () {
                this.element.nativeElement.classList.add(cssClass);
                ngOnInit?.call(this);
            };
        })(target.prototype.ngOnInit);
    };
}

/** @internal */
/* istanbul ignore next */
function CSSModifier(options) {
    return (target, propertyKey) => {
        const propertyKeyName = propertyKey.toString();
        const propertyKeyKebabCase = camelToKebabCase(propertyKeyName);
        const propertySymbol = decoratorPropertyName('CSSModifier', propertyKeyName);
        const propertyDefaultValue = decoratorPropertyName('CSSModifier', `${propertyKeyName}_default`);
        function createModifierClass(value) {
            if (typeof value === 'boolean' || (typeof value === 'string' && value.length > 0)) {
                let modifier = typeof value === 'boolean' ? propertyKeyKebabCase : value;
                if (options?.withPrefix) {
                    modifier = `${propertyKeyName}-${modifier}`;
                }
                if (options?.withComponentPrefix ?? true) {
                    return `${this[CSS_COMPONENT_NAME]}--${modifier}`;
                }
                return modifier;
            }
            return null;
        }
        function getter() {
            return this[propertySymbol];
        }
        function setter(value) {
            this[propertyDefaultValue] ??= value;
            const currentValue = this[propertySymbol] ?? null;
            const nextValue = value ?? this[propertyDefaultValue] ?? null;
            if (nextValue !== currentValue) {
                const currentModifierClass = createModifierClass.call(this, currentValue);
                if (currentModifierClass !== null) {
                    this.element.nativeElement.classList.remove(currentModifierClass);
                }
                const newModifierClass = createModifierClass.call(this, nextValue);
                if (newModifierClass !== null && (typeof nextValue === 'boolean' ? nextValue : true)) {
                    this.element.nativeElement.classList.add(newModifierClass);
                }
            }
            this[propertySymbol] = nextValue;
        }
        Object.defineProperty(target, propertyKey, {
            get: getter,
            set: setter,
        });
    };
}

class Logger {
    constructor(name) {
        this.name = name;
        this.prefix = `[${this.name}]`;
        this.isEnabled = isDevMode();
    }
    log(...data) {
        this.handle('log', data);
    }
    info(...data) {
        this.handle('info', data);
    }
    warn(...data) {
        this.handle('warn', data);
    }
    error(...data) {
        this.handle('error', data);
    }
    debug(...data) {
        this.handle('debug', data);
    }
    group(name, groupHandler, collapsed = true) {
        if (!this.isEnabled)
            return;
        console[collapsed ? 'groupCollapsed' : 'group'](this.prefix, name);
        groupHandler.apply(this, [console.info]);
        console.groupEnd();
    }
    enable() {
        this.isEnabled = true;
    }
    disable() {
        this.isEnabled = false;
    }
    handle(method, data) {
        if (!this.isEnabled)
            return;
        console[method](this.prefix, ...data);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { CSSComponent, CSSModifier, CSS_COMPONENT_NAME, Logger, camelToKebabCase, decoratorPropertyName, deepmerge, fastDeepEqual, getCSSVariables, getEnumValues, toggleFormDisabledState };
